<template>
    <!-- Two grid items, not one wrapping div — lets the terminal-state chips below stretch (w-full) to match the Link/dismiss pair's width -->
    <div class="flex min-w-0 items-center gap-3 py-3">
        <span class="profile-link-avatar" :class="suggestion.action === 'link' ? 'profile-link-avatar--link' : 'profile-link-avatar--request'" aria-hidden="true" translate="no">
            {{ initials }}
        </span>
        <div class="min-w-0">
            <p class="truncate nadia-chat-semibold text-valence-grey-850">{{ suggestion.name }}</p>
            <p class="truncate scaffold-details-small text-valence-grey-600">{{ subtext }}</p>
        </div>
    </div>

    <div class="flex items-center justify-end gap-1.5 py-3">
        <StatusChip v-if="status === 'linked'" state="done" size="md" class="w-full justify-center">
            <template #leading><i class="bi bi-check-lg" aria-hidden="true" /></template>
            Linked
        </StatusChip>
        <StatusChip v-else-if="status === 'shared'" state="neutral" size="md" class="w-full justify-center">
            <template #leading><i :class="orgHasMsTeams ? 'bi bi-send' : 'bi bi-clipboard-check'" aria-hidden="true" /></template>
            {{ sharedTerminalLabel }}
        </StatusChip>

        <!-- Temporary override of BaseButton's primary theme: forces the grey-850 (#262626) dark fill
             from the Figma design until a dark button variant exists in the BaseButton design system. -->
        <BaseButton
            v-else-if="suggestion.action === 'link'"
            theme="primary"
            class="relative flex flex-1 items-center justify-center bg-valence-grey-850 hover:bg-valence-grey-800"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-link-btn-${suggestion.target_user_id}`"
            @click="handleLink">
            <span :class="{ invisible: isLinking }">Link</span>
            <span v-if="isLinking" class="absolute inset-0 flex items-center justify-center">
                <LoadingSpinner class="size-4" />
            </span>
        </BaseButton>

        <BaseButton
            v-else
            theme="neutral"
            class="relative flex flex-1 items-center justify-center rounded-full"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-request-btn-${suggestion.target_user_id}`"
            @click="handleShare">
            <span :class="{ invisible: isGeneratingLink }" class="inline-flex items-center gap-2">
                <img v-if="orgHasMsTeams" :src="msTeamsIcon" alt="" class="size-5 shrink-0" />
                <i v-else class="bi bi-link-45deg text-lg/none" aria-hidden="true"></i>
                {{ shareButtonLabel }}
            </span>
            <span v-if="isGeneratingLink" class="absolute inset-0 flex items-center justify-center">
                <LoadingSpinner class="size-4" />
            </span>
        </BaseButton>
        <button
            v-if="status === 'idle'"
            type="button"
            title="Dismiss suggestion"
            aria-label="Dismiss suggestion"
            class="flex size-7 items-center justify-center rounded-full text-valence-grey-600 transition-colors hover:bg-valence-grey-50 hover:text-valence-grey-850 disabled:opacity-40"
            :disabled="isBusy || disabled"
            :data-test-id="`profile-dismiss-btn-${suggestion.target_user_id}`"
            @click="handleDismiss">
            <LoadingSpinner v-if="isDismissing" class="size-3.5" />
            <i v-else class="bi bi-x-lg text-sm/none" aria-hidden="true"></i>
        </button>
    </div>
</template>

<script setup lang="ts">
import msTeamsIcon from "~img/ms-teams-icon.png";
import { useFetch } from "~vue/api";
import type { UpdateSuggestionActionRequest } from "~vue/api/generated/connections";
import { copyShareUrl, openTeamsShare } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { useProfileShareLink } from "~vue/CollaborationProfile/composables/useProfileShareLink";
import BaseButton from "~vue/components/BaseButton.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { firstNameOf, getFirstLastInitials } from "~vue/utils/stringUtils";
import { computed, ref } from "vue";

import type { CollabProfileLinkRequest, OwnProfileShare, ProfileLinkSuggestion, ShareFailureReason, ShareTarget, SuggestionStatus } from "./profileLinkTypes";

const props = defineProps<{
    suggestion: ProfileLinkSuggestion;
    status: SuggestionStatus;
    ownProfileShare: OwnProfileShare | null;
    orgHasMsTeams: boolean;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    linked: [suggestion: ProfileLinkSuggestion];
    "link-failed": [suggestion: ProfileLinkSuggestion];
    shared: [suggestion: ProfileLinkSuggestion, target: ShareTarget];
    "share-failed": [suggestion: ProfileLinkSuggestion, reason: ShareFailureReason];
    dismissed: [suggestion: ProfileLinkSuggestion];
    "dismiss-failed": [suggestion: ProfileLinkSuggestion];
}>();

const linkUrl = computed(() => `/v1/collaboration-profiles/${props.suggestion.target_user_id}/ensure-link`);
const linkPayload = computed<CollabProfileLinkRequest>(() => ({ connection_id: props.suggestion.connection_id }));
const { error: linkError, isFetching: isLinking, execute: executeLink } = useFetch(linkUrl, { immediate: false }).post(linkPayload).json();

const dismissPayload = ref<UpdateSuggestionActionRequest>();
// The action endpoint returns 204 No Content: .json() would throw on the empty body, and useFetch
// parses before checking response.ok, so a committed dismissal would be reported as a failure.
const {
    error: dismissError,
    statusCode: dismissStatus,
    isFetching: isDismissing,
    execute: executeDismiss,
} = useFetch("/v1/connections/profile-link-suggestions/action", { immediate: false }).post(dismissPayload);

const { createTargetedInvitation, isGeneratingLink } = useProfileShareLink({
    profileId: computed(() => props.ownProfileShare?.profile_id),
});

const isBusy = computed(() => isLinking.value || isGeneratingLink.value || isDismissing.value);

async function handleLink() {
    if (isBusy.value) {
        return;
    }
    await executeLink();

    // Includes the stale-suggestion race (profile no longer viewable → ensure-link 404).
    if (linkError.value) {
        emit("link-failed", props.suggestion);
        return;
    }
    emit("linked", props.suggestion);
}

// Share the viewer's own profile with a colleague: mint a targeted invitation, then deliver
// its link via Teams (if the org uses Teams) or the clipboard, with recipient-personalized copy.
async function handleShare() {
    if (isBusy.value) {
        return;
    }
    if (!props.ownProfileShare?.can_share) {
        emit("share-failed", props.suggestion, "not_publishable");
        return;
    }

    const shareUrl = await createTargetedInvitation(props.suggestion.target_user_id);
    if (!shareUrl) {
        emit("share-failed", props.suggestion, "invite");
        return;
    }

    const message = `Hi ${firstNameOf(props.suggestion.name)}! Here's my Collaboration Profile so you can see how I like to work. Share yours back so I can do the same?`;
    if (props.orgHasMsTeams) {
        openTeamsShare(shareUrl, message);
        emit("shared", props.suggestion, "teams");
        return;
    }

    if (!(await copyShareUrl(shareUrl, message))) {
        emit("share-failed", props.suggestion, "clipboard");
        return;
    }
    emit("shared", props.suggestion, "link");
}

async function handleDismiss() {
    if (isBusy.value) {
        return;
    }
    dismissPayload.value = { target_user_id: props.suggestion.target_user_id, action: "dismissed" };
    await executeDismiss();

    if (dismissError.value || dismissStatus.value === null) {
        emit("dismiss-failed", props.suggestion);
        return;
    }
    emit("dismissed", props.suggestion);
}

const initials = computed(() => getFirstLastInitials(props.suggestion.name));
const shareButtonLabel = computed(() => (props.orgHasMsTeams ? "Send" : "Copy"));
const sharedTerminalLabel = computed(() => (props.orgHasMsTeams ? "Sent" : "Copied"));

const subtext = computed(() => {
    const { title, source, meeting_count: meetingCount } = props.suggestion;
    if (source === "profile_linker") {
        return title ? `${title} · Linked your collaboration profile` : "Linked your collaboration profile";
    }
    if (source === "calendar") {
        return `From calendar · ${meetingCount === 1 ? "1 meeting" : `${meetingCount} meetings`}`;
    }
    return title ? `${title} · In your people` : "In your people";
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .profile-link-avatar {
        /* details + font-serif is an exception to the typography */
        @apply flex size-9 shrink-0 items-center justify-center rounded-full details font-serif;
    }

    .profile-link-avatar--link {
        @apply bg-valence-green-olive-light text-valence-green-800;
    }

    .profile-link-avatar--request {
        @apply bg-valence-grey-100 text-valence-grey-600;
    }
}
</style>
